import React, { useState } from 'react';
import { X, Tag, Loader2, CheckCircle2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Link } from 'wouter';
import { motion, AnimatePresence } from 'motion/react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc, doc, getDoc, updateDoc, serverTimestamp, increment, getDocs, arrayUnion, writeBatch } from 'firebase/firestore';
import { createNotification } from '../lib/notifications';
import toast from 'react-hot-toast';

interface CheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CheckoutModal({ isOpen, onClose }: CheckoutModalProps) {
  const { items, totalPrice, clearCart } = useCart();
  const { user, userData, signInWithGoogle } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    pincode: '',
    businessType: 'Retail',
    gstNumber: '',
    paymentMethod: 'Online Payment',
  });
  
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ id: string, code: string, discountAmount: number, type: 'percent' | 'flat', value: number, isSingleUse: boolean } | null>(null);
  const [couponError, setCouponError] = useState('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [placedOrderNumber, setPlacedOrderNumber] = useState('');

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setSuccess(false);
      setAppliedCoupon(null);
      setSelectedAddressId(null);
      setCouponCode('');
      setCouponError('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (selectedAddressId) setSelectedAddressId(null);
  };

  const handleSelectAddress = (address: any) => {
    setSelectedAddressId(address.id);
    setFormData({
      ...formData,
      name: address.name,
      phone: address.phone,
      address: address.address,
      pincode: address.pincode,
    });
  };

  const handleApplyCoupon = async () => {
    setCouponError('');
    const code = couponCode.trim().toUpperCase();
    
    if (!code) {
      setCouponError('Please enter a coupon code');
      return;
    }

    setIsApplyingCoupon(true);
    try {
      const couponDoc = await getDoc(doc(db, 'coupons', code));
      if (!couponDoc.exists()) {
        setCouponError('Invalid coupon code');
        setIsApplyingCoupon(false);
        return;
      }

      const coupon = couponDoc.data();
      if (!coupon.isActive) {
        setCouponError('This coupon is no longer active');
        setIsApplyingCoupon(false);
        return;
      }

      let discount = 0;
      if (coupon.type === 'percent') {
        discount = Math.round(totalPrice * (coupon.value / 100));
      } else {
        discount = coupon.value;
      }

      // Ensure discount doesn't exceed total price
      discount = Math.min(discount, totalPrice);

      setAppliedCoupon({
        id: couponDoc.id,
        code: coupon.code,
        discountAmount: discount,
        type: coupon.type,
        value: coupon.value,
        isSingleUse: coupon.isSingleUse
      });
      setCouponCode('');
    } catch (err) {
      console.error(err);
      setCouponError('Error verifying coupon');
    }
    setIsApplyingCoupon(false);
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  const finalTotal = appliedCoupon ? Math.max(0, totalPrice - appliedCoupon.discountAmount) : totalPrice;
  const gstAmount = Math.round(finalTotal * 0.0476); // Reverse calculate 5% from inclusive price (approx 4.76%)

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') return;
      console.error("Google Sign-In Error:", error);
    }
  };

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    if (!user) {
      try {
        await signInWithGoogle();
      } catch (error: any) {
        setIsSubmitting(false);
        if (error.code === 'auth/popup-closed-by-user') return;
        console.error("Auth error during checkout", error);
        return;
      }
    }

    try {
      const orderNumber = Math.floor(10000000 + Math.random() * 90000000).toString();
      setPlacedOrderNumber(orderNumber);
      
      // Auto-route to Facility Center based on Pincode
      let assignedFacilityId = '';
      try {
        const facilitiesSnap = await getDocs(collection(db, 'facilityCenters'));
        const facilities = facilitiesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const matchedFacility = facilities.find((f: any) => 
          f.isActive && f.pincodes && Array.isArray(f.pincodes) && f.pincodes.includes(formData.pincode.trim())
        );
        if (matchedFacility) {
          assignedFacilityId = matchedFacility.id;
        }
      } catch (err) {
        console.error("Error routing facility", err);
      }

      // Calculate accurate GST from inclusive prices
      const totalGst = items.reduce((acc, item) => {
        const itemTotal = item.price * item.quantity;
        const rate = item.gstPercentage || 5;
        const tax = itemTotal - (itemTotal / (1 + rate / 100));
        return acc + tax;
      }, 0);

      // USE BATCH FOR ALL WRITES
      const batch = writeBatch(db);

      const orderRef = doc(collection(db, 'orders'));
      const orderData = {
        orderNumber,
        customerName: formData.name,
        customerPhone: formData.phone,
        customerAddress: formData.address,
        pincode: formData.pincode.trim(),
        facilityCenterId: assignedFacilityId,
        businessType: formData.businessType,
        gstNumber: formData.businessType === 'Restaurant' ? formData.gstNumber : '',
        paymentMethod: formData.paymentMethod,
        items: items.map(item => ({
          name: item.name,
          productId: item.id,
          price: item.price,
          costPrice: item.costPrice || 0,
          gstPercentage: item.gstPercentage || 5,
          hsnCode: item.hsnCode || '',
          quantity: item.quantity,
          selectedWeight: item.selectedWeight
        })),
        subtotal: totalPrice,
        discountAmount: appliedCoupon ? appliedCoupon.discountAmount : 0,
        couponCode: appliedCoupon ? appliedCoupon.code : '',
        subtotalAfterDiscount: finalTotal,
        gstAmount: Math.round(totalGst),
        totalAmount: finalTotal,
        status: 'Received',
        userId: user?.uid,
        createdAt: serverTimestamp()
      };

      batch.set(orderRef, orderData);

      // Create notifications
      const adminNotifRef = doc(collection(db, 'notifications'));
      batch.set(adminNotifRef, {
        title: 'New Order Received',
        message: `Order #${orderNumber} placed by ${formData.name}`,
        type: 'order',
        read: false,
        createdAt: serverTimestamp(),
        referenceId: orderRef.id
      });

      if (assignedFacilityId) {
        const facilityNotifRef = doc(collection(db, 'notifications'));
        batch.set(facilityNotifRef, {
          title: 'Order Routed to Unit',
          message: `Order #${orderNumber} automatically routed based on pincode ${formData.pincode}`,
          type: 'facility',
          read: false,
          createdAt: serverTimestamp(),
          referenceId: orderRef.id
        });
      }

      // Decrement stock for each item
      for (const item of items) {
        if (item.id) {
          const productRef = doc(db, 'products', item.id);
          batch.update(productRef, {
            stock: increment(-item.quantity)
          });
        }
      }

      // Update coupon if single use
      if (appliedCoupon && appliedCoupon.isSingleUse) {
        batch.update(doc(db, 'coupons', appliedCoupon.id), {
          isActive: false
        });
      }

      // Save address automatically if user is logged in
      if (user) {
        const userRef = doc(db, 'users', user.uid);
        const newAddress = {
          id: Math.random().toString(36).substr(2, 9),
          name: formData.name,
          phone: formData.phone,
          address: formData.address,
          pincode: formData.pincode.trim(),
          isDefault: false
        };
        
        const isDuplicate = userData?.savedAddresses?.some((a: any) => 
          a.address === formData.address && a.pincode === formData.pincode.trim()
        );

        if (!isDuplicate) {
          batch.update(userRef, {
            savedAddresses: arrayUnion(newAddress),
            updatedAt: serverTimestamp()
          });
        }
      }

      // Commit all writes at once
      await batch.commit();

      setSuccess(true);
      toast.success('Order Placed Successfully! 🌶️', {
        id: 'order-success-toast',
        duration: 5000,
      });
      clearCart();
      
      // We don't auto-close immediately to let user see the order number and go to orders if they want
    } catch (err) {
      console.error('Error placing order', err);
      toast.error('Failed to place order. Please try again.');
      setIsSubmitting(false);
    }
  };

  const isIframe = typeof window !== 'undefined' && window.self !== window.top;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-0 md:p-4 font-sans">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black cursor-pointer"
        />
        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          className="relative bg-white w-full max-w-lg shadow-2xl flex flex-col h-full md:h-auto md:max-h-[85vh] md:rounded-2xl border-t border-[#E5E0D8] md:border border-[#E5E0D8] overflow-hidden"
        >
          <div className="flex justify-between items-center p-4 sm:p-5 border-b border-[#E5E0D8] bg-[#FAF7F2] sticky top-0 z-10">
            <h2 className="text-base sm:text-lg font-serif font-bold text-[#1F1F1F] tracking-tight uppercase">
              {success ? 'Order Confirmation' : 'Checkout & Confirm Order'}
            </h2>
            <button onClick={onClose} className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 hover:text-black transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {success ? (
            <div className="p-8 xs:p-10 flex flex-col items-center justify-center text-center flex-1 bg-transparent overflow-y-auto relative h-full">
              {/* Confetti-like bits - ENHANCED EXPLOSION */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {[...Array(40)].map((_, i) => (
                  <motion.div
                    key={i}
                    initial={{ 
                      top: '50%', 
                      left: '50%', 
                      scale: 0,
                      rotate: 0,
                      opacity: 1 
                    }}
                    animate={{ 
                      top: [`50%`, `${Math.random() * 100}%`],
                      left: [`50%`, `${Math.random() * 100}%`],
                      scale: [0, 1.5, 0.5, 0],
                      rotate: [0, 180, 360, 720],
                      opacity: [0, 1, 1, 0]
                    }}
                    transition={{ 
                      duration: 3 + Math.random() * 3,
                      repeat: Infinity,
                      repeatDelay: Math.random() * 2,
                      ease: "easeOut"
                    }}
                    className={`absolute w-2 h-2 ${i % 4 === 0 ? 'rounded-full' : i % 4 === 1 ? 'rounded-sm' : 'clip-path-triangle'} ${i % 3 === 0 ? 'bg-spice-accent' : i % 3 === 1 ? 'bg-spice-red' : 'bg-white'}`}
                    style={{
                      clipPath: i % 4 === 2 ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'none'
                    }}
                  />
                ))}
              </div>

              <motion.div 
                initial={{ scale: 0, rotate: -180 }} 
                animate={{ scale: 1.1, rotate: 0 }} 
                transition={{ 
                  type: "spring", 
                  damping: 8, 
                  stiffness: 150,
                  delay: 0.1
                }}
                className="relative z-10"
              >
                <div className="w-24 h-24 bg-spice-accent/10 rounded-[30px] flex items-center justify-center mb-6 border-2 border-spice-accent/20 shadow-2xl shadow-spice-accent/30">
                  <motion.div
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.5, type: "spring" }}
                  >
                    <CheckCircle2 className="w-12 h-12 text-spice-accent" />
                  </motion.div>
                </div>
                {/* Secondary Pulse rings */}
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0.5 }}
                  animate={{ scale: 2.2, opacity: 0 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeOut" }}
                  className="absolute inset-0 border-2 border-spice-accent/40 rounded-[30px]"
                />
                <motion.div 
                  initial={{ scale: 0.8, opacity: 0.3 }}
                  animate={{ scale: 3.0, opacity: 0 }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeOut", delay: 0.5 }}
                  className="absolute inset-0 border border-white/20 rounded-[30px]"
                />
              </motion.div>

              <motion.h3 
                initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ delay: 0.4, duration: 0.8 }}
                className="text-3xl font-serif font-bold text-white mb-2 uppercase tracking-tight relative z-10"
              >
                Order <span className="serif italic font-normal text-spice-accent">Received</span>
              </motion.h3>
              
              {placedOrderNumber && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6, type: "spring" }}
                  className="bg-white/10 px-5 py-2.5 rounded-xl mb-4 border border-white/10 shadow-xl backdrop-blur-sm relative z-10"
                >
                  <p className="text-[10px] text-spice-accent font-black uppercase tracking-[0.3em]">Confirmation ID #{placedOrderNumber}</p>
                </motion.div>
              )}
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="text-white/40 mb-8 font-black uppercase tracking-[0.2em] text-[9px] max-w-xs leading-relaxed relative z-10"
              >
                Your selection of the finest spices is now secured. We are preparing to dispatch your essence to your doorstep.
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2 }}
                className="flex flex-col w-full gap-3 max-w-xs relative z-10"
              >
                <Link to="/orders" onClick={onClose} className="w-full bg-white text-[#09090b] py-3.5 rounded-xl font-black uppercase tracking-[0.15em] text-[10px] text-center hover:bg-spice-accent hover:text-white transition-all transform hover:-translate-y-0.5 shadow-xl shadow-white/5 active:scale-95">
                  Track Dispatched Spices
                </Link>
                <button onClick={onClose} className="w-full border border-white/10 text-white/40 py-3 rounded-xl font-black uppercase tracking-[0.15em] text-[9px] hover:text-white hover:bg-white/5 transition-all active:scale-95">
                  Dismiss Protocol
                </button>
              </motion.div>
            </div>
          ) : !user ? (
            <div className="p-16 flex flex-col items-center justify-center text-center flex-1 bg-transparent">
              <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mb-10 border border-white/10">
                <Loader2 className="w-8 h-8 text-white/20 animate-spin-slow" />
              </div>
              <h3 className="text-3xl font-serif font-bold text-white mb-6 uppercase tracking-tight">Login Required</h3>
              <p className="text-white/40 mb-10 font-bold uppercase tracking-[0.15em] text-xs max-w-xs leading-loose italic">Please sign in to complete your transaction and track your spice delivery.</p>
              
              <div className="flex flex-col w-full gap-4">
                <button 
                  onClick={handleGoogleSignIn}
                  className="w-full bg-spice-accent text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-[11px] hover:scale-105 transition-all shadow-xl shadow-spice-accent/40"
                >
                  Sign In with Google
                </button>
                
                <Link 
                  to="/login"
                  className="w-full border border-white/10 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] text-[11px] hover:bg-white/5 transition-all"
                >
                  Alternative Login
                </Link>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-y-auto p-4 sm:p-6 bg-white flex-1 custom-scrollbar">
                <form id="checkout-form" onSubmit={handleCheckout} className="space-y-5">
                  {/* Saved Addresses Section */}
                  {userData?.savedAddresses?.length > 0 && (
                    <div>
                      <label className="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">Saved Addresses</label>
                      <div className="flex flex-col gap-2">
                        {userData.savedAddresses.map((addr: any) => (
                          <div 
                            key={addr.id}
                            onClick={() => handleSelectAddress(addr)}
                            className={`p-3 border rounded-lg transition-all cursor-pointer relative ${
                              selectedAddressId === addr.id 
                                ? 'border-[#D97736] bg-[#FAF7F2] ring-1 ring-[#D97736]' 
                                : 'border-[#E5E0D8] bg-white hover:border-gray-400'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-1">
                              <p className="text-xs font-bold text-[#1F1F1F]">{addr.name}</p>
                              {selectedAddressId === addr.id && (
                                <div className="w-4 h-4 bg-[#D97736] rounded-full flex items-center justify-center">
                                  <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                                </div>
                              )}
                            </div>
                            <p className="text-[10px] text-gray-500 font-semibold">{addr.phone}</p>
                            <p className="text-[11px] text-gray-700 leading-relaxed font-normal">{addr.address}, {addr.pincode}</p>
                          </div>
                        ))}
                      </div>
                      <div className="relative my-4">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-[9px] font-bold uppercase tracking-wider">
                          <span className="px-3 bg-white text-gray-400">Or deliver to new address</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">Customer Details</label>
                    <div className="grid grid-cols-1 gap-2.5">
                      <input 
                        required 
                        name="name" 
                        placeholder="Full Name"
                        value={formData.name} 
                        onChange={handleChange} 
                        type="text" 
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-[#D5D0C8] rounded focus:border-[#D97736] outline-none bg-white text-[#1F1F1F] font-medium placeholder:text-gray-400" 
                      />
                      <input 
                        required 
                        name="phone" 
                        placeholder="Phone Number (10 Digits)"
                        value={formData.phone} 
                        onChange={handleChange} 
                        type="tel" 
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-[#D5D0C8] rounded focus:border-[#D97736] outline-none bg-white text-[#1F1F1F] font-medium placeholder:text-gray-400" 
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-2">Delivery Address</label>
                    <div className="space-y-2.5">
                      <textarea 
                        required 
                        name="address" 
                        placeholder="House/Flat No., Street, Landmark, City"
                        value={formData.address} 
                        onChange={handleChange} 
                        rows={2} 
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-[#D5D0C8] rounded focus:border-[#D97736] outline-none bg-white text-[#1F1F1F] font-medium placeholder:text-gray-400 resize-none"
                      ></textarea>
                      <input 
                        required 
                        name="pincode" 
                        placeholder="6-Digit Postal PIN Code"
                        maxLength={6}
                        value={formData.pincode} 
                        onChange={handleChange} 
                        type="text" 
                        className="w-full px-3.5 py-2.5 text-xs sm:text-sm border border-[#D5D0C8] rounded focus:border-[#D97736] outline-none bg-white text-[#1F1F1F] font-medium placeholder:text-gray-400" 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Order Type</label>
                      <select name="businessType" value={formData.businessType} onChange={handleChange} className="w-full px-3.5 py-2.5 text-xs border border-[#D5D0C8] rounded outline-none bg-white text-[#1F1F1F] font-medium cursor-pointer">
                        <option value="Retail">Retail / Household</option>
                        <option value="Restaurant">Hotel / Restaurant (B2B)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Payment Method</label>
                      <select name="paymentMethod" value={formData.paymentMethod} onChange={handleChange} className="w-full px-3.5 py-2.5 text-xs border border-[#D5D0C8] rounded outline-none bg-white text-[#1F1F1F] font-medium cursor-pointer">
                        <option value="Online Payment">Online Payment (UPI/Cards)</option>
                        <option value="COD">Cash on Delivery (COD)</option>
                      </select>
                    </div>
                  </div>

                  {/* Coupon Code Section */}
                  <div className="bg-[#FAF7F2] p-3.5 rounded-lg border border-[#E5E0D8]">
                    <label className="block text-[9px] font-bold uppercase tracking-wider text-[#D97736] mb-2 flex items-center">
                      <Tag className="w-3.5 h-3.5 mr-1.5" />
                      Have a Promo Code?
                    </label>
                    
                    {!appliedCoupon ? (
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                          placeholder="ENTER CODE" 
                          disabled={isApplyingCoupon}
                          className="flex-1 px-3 py-2 text-xs border border-[#D5D0C8] rounded focus:border-[#D97736] outline-none bg-white text-[#1F1F1F] font-bold uppercase tracking-wider" 
                        />
                        <button 
                          type="button" 
                          onClick={handleApplyCoupon}
                          disabled={isApplyingCoupon}
                          className="px-4 py-2 btn-terracotta bg-[#D97736] text-white font-bold uppercase tracking-wider text-xs rounded hover:bg-[#C85A17] transition-all min-w-[70px]"
                        >
                          {isApplyingCoupon ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Apply'}
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-green-50 p-2.5 rounded border border-green-200">
                        <span className="font-bold text-green-700 text-xs">Coupon Applied: -Rs. {appliedCoupon.discountAmount.toFixed(2)}</span>
                        <button type="button" onClick={removeCoupon} className="text-gray-500 hover:text-black">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {couponError && <p className="text-red-600 text-[10px] font-bold mt-1.5 uppercase">{couponError}</p>}
                  </div>
                </form>
              </div>
              
              <div className="p-4 sm:p-5 bg-[#FAF7F2] border-t border-[#E5E0D8]">
                 <div className="flex justify-between items-center mb-3">
                    <div className="flex flex-col">
                      <span className="text-[9px] uppercase font-bold tracking-wider text-gray-500">Total Payable</span>
                      <span className="text-xl sm:text-2xl font-black text-[#1F1F1F]">Rs. {finalTotal.toFixed(2)}</span>
                    </div>
                    {appliedCoupon && (
                      <div className="text-right">
                        <span className="text-[9px] uppercase font-bold text-green-700">You Saved</span>
                        <span className="block text-sm font-bold text-green-700 leading-none mt-0.5">Rs. {appliedCoupon.discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                 </div>
                 <button
                    type="submit"
                    form="checkout-form"
                    disabled={isSubmitting}
                    className="w-full btn-terracotta bg-[#D97736] hover:bg-[#C85A17] text-white font-bold uppercase tracking-wider text-xs sm:text-sm py-3.5 rounded shadow-md active:scale-95 transition-all flex justify-center items-center gap-2"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                      <>
                        <span>{formData.paymentMethod === 'Online Payment' ? `Pay Securely` : 'Confirm Order'}</span>
                        <CheckCircle2 className="w-4 h-4" />
                      </>
                    )}
                  </button>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
